import type React from 'react';
import { IconMap } from '@/components/Icon/iconMap';
import type { PersonNameChildAttribute } from '@/components/Input/PersonName/types';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import type { SupportedCountry } from '@/constants/country';
import type { ValueType } from '@/features/section-attribute/types';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormAddressItem } from '../CFormItems/impl/CFormAddressItem';
import { CFormBadgeItem } from '../CFormItems/impl/CFormBadgeItem';
import { CFormBankAccountItem } from '../CFormItems/impl/CFormBankAccountItem';
import { CFormBirthdayItem } from '../CFormItems/impl/CFormBirthdayItem';
import { CFormBpSelectItem } from '../CFormItems/impl/CFormBpSelectItem';
import { CFormByteSizeItem } from '../CFormItems/impl/CFormByteSizeItem';
import { CFormCommuteRouteListItem } from '../CFormItems/impl/CFormCommuteRouteListItem';
import { CFormContractItem } from '../CFormItems/impl/CFormContractItem';
import { CFormCountryItem } from '../CFormItems/impl/CFormCountryItem';
import { CFormCurrencyItem } from '../CFormItems/impl/CFormCurrencyItem';
import { CFormDateItem } from '../CFormItems/impl/CFormDateItem';
import { CFormDateTimeItem } from '../CFormItems/impl/CFormDateTimeItem';
import { CFormDepartmentListItem } from '../CFormItems/impl/CFormDepartmentListItem';
import { CFormDepartmentSelectItem } from '../CFormItems/impl/CFormDepartmentSelectItem';
import { CFormDeviceCatalogSelectItem } from '../CFormItems/impl/CFormDeviceCatalogSelectItem';
import { CFormEmailItem } from '../CFormItems/impl/CFormEmailItem';
import { CFormEmailListItem } from '../CFormItems/impl/CFormEmailListItem';
import { CFormEmergencyContactListItem } from '../CFormItems/impl/CFormEmergencyContactListItem';
import { CFormEmployeeSubIdListItem } from '../CFormItems/impl/CFormEmployeeSubIdListItem';
import { CFormExtensionNumberListItem } from '../CFormItems/impl/CFormExtensionNumberListItem';
import { CFormFamilyPersonalInfoListItem } from '../CFormItems/impl/CFormFamilyPersonalInfoListItem';
import { CFormFileItem } from '../CFormItems/impl/CFormFileItem';
import { CFormFileListItem } from '../CFormItems/impl/CFormFileListItem';
import { CFormFolderLinkItem } from '../CFormItems/impl/CFormFolderLinkItem';
import { CFormGradeItem } from '../CFormItems/impl/CFormGradeItem';
import { CFormHometownListItem } from '../CFormItems/impl/CFormHometownListItem';
import { CFormImageItem } from '../CFormItems/impl/CFormImageItem';
import { CFormJobCompetencieItem } from '../CFormItems/impl/CFormJobCompetencieItem';
import { CFormJobFunctionListItem } from '../CFormItems/impl/CFormJobFunctionListItem';
import { CFormJobGradeItem } from '../CFormItems/impl/CFormJobGradeItem';
import { CFormJobLevelItem } from '../CFormItems/impl/CFormJobLevelItem';
import { CFormJobTitleItem } from '../CFormItems/impl/CFormJobTitleItem';
import { CFormJobTypeListItem } from '../CFormItems/impl/CFormJobTypeListItem';
import { CFormLeaveOfAbsenceListItem } from '../CFormItems/impl/CFormLeaveOfAbsenceListItem';
import { CFormMultiCountryItem } from '../CFormItems/impl/CFormMultiCountryItem';
import { CFormMultiSelectItem } from '../CFormItems/impl/CFormMultiSelectItem';
import { CFormMultiTextItem } from '../CFormItems/impl/CFormMultiTextItem';
import { CFormNumberItem } from '../CFormItems/impl/CFormNumberItem';
import { CFormNumberWithUnitItem } from '../CFormItems/impl/CFormNumberWithUnitItem';
import { CFormPersonNameItem } from '../CFormItems/impl/CFormPersonNameItem';
import { CFormPersonSelectItem } from '../CFormItems/impl/CFormPersonSelectItem';
import { CFormPhoneItem } from '../CFormItems/impl/CFormPhoneItem';
import { CFormPhoneListItem } from '../CFormItems/impl/CFormPhoneListItem';
import { CFormPlaceMultiSelectItem } from '../CFormItems/impl/CFormPlaceMultiSelectItem';
import { CFormPlaceMultiSelectWithPrimaryItem } from '../CFormItems/impl/CFormPlaceMultiSelectWithPrimaryItem';
import { CFormPlaceSelectItem } from '../CFormItems/impl/CFormPlaceSelectItem';
import { CFormPostalCodeItem } from '../CFormItems/impl/CFormPostalCodeItem';
import { CFormSelectItem } from '../CFormItems/impl/CFormSelectItem';
import { CFormSnsAccountListItem } from '../CFormItems/impl/CFormSnsAccountListItem';
import { CFormStateProvinceItem } from '../CFormItems/impl/CFormStateProvinceItem';
import { CFormSwitchItem } from '../CFormItems/impl/CFormSwitchItem';
import { CFormTextAreaItem } from '../CFormItems/impl/CFormTextAreaItem';
import { CFormTextItem } from '../CFormItems/impl/CFormTextItem';
import { CFormUrlItem } from '../CFormItems/impl/CFormUrlItem';
import { CFormWorkerTypeSelectItem } from '../CFormItems/impl/CFormWorkerTypeSelectItem';
import { CFormWorkLocationItem } from '../CFormItems/impl/CFormWorkLocationItem';
import { CFormWorkModeItem } from '../CFormItems/impl/CFormWorkModeItem';
import { coerceAttributeValue } from './coerceAttributeValue';
import type { Attribute, StoreValues, UpdateSectionAttribute, Value, ValueTypeOptions } from './type';

/**
 * ベースプロパティの型定義
 */
export interface BaseProps {
  id: string;
  label: string;
  required: boolean;
  readonly: boolean;
  colSpan: 1 | 2 | 3 | 4 | 5 | 6 | 7 | undefined;
  titleIcon?: typeof IconMap extends Record<infer K, unknown> ? K : never;
}

/**
 * ValueType=CUSTOMを設定した項目に対して、ドメイン独自のレンダリングを実施する場合に利用する
 */
export type ProvisionalValueTypeRenderer = (attribute: Attribute, baseProps: BaseProps, getCurrentAttribute: (attributeKey: string, sectionKey: string, storeValues: StoreValues) => Attribute | undefined) => React.ReactElement | null;
export type AttributeRenderInterceptorContext = {
  sectionKey: string;
  attr: Attribute;
  baseProps: BaseProps;
  handleValueChange: (newValue: unknown) => void;
  updateSectionAttribute: UpdateSectionAttribute;
  storeValues: StoreValues;
};
export type AttributeRenderInterceptor = (context: AttributeRenderInterceptorContext) => React.ReactElement | null;
interface Props {
  attributes: Attribute[];
  sectionKey: string;
  updateSectionAttribute: UpdateSectionAttribute;
  storeValues: StoreValues;
  /** valueType固有のオプション */
  options?: ValueTypeOptions;
  /** ValueType=CUSTOMを設定した項目に対して、ドメイン独自のレンダリングを実施する場合に利用する */
  provisionalValueTypeRenderer?: ProvisionalValueTypeRenderer;
  /**
   * valueType の分岐の前に呼ばれる。非 null を返すと既定のレンダリングをスキップする。
   */
  attributeRenderInterceptor?: AttributeRenderInterceptor;
}

/**
 * 汎用属性レンダラー
 *
 * バックエンドのvalueTypeに基づいて動的にフォームコンポーネントを生成します。
 *
 * ## 使用例
 * ```tsx
 * <AttributeRenderer
 *   attributes={section.attributes}
 *   sectionKey={section.sectionKey}
 *   actions={store.actions}
 *   storeValues={store.values}
 * />
 * ```
 */
export function AttributeRenderer({
  attributes,
  sectionKey,
  updateSectionAttribute,
  storeValues,
  provisionalValueTypeRenderer,
  attributeRenderInterceptor,
  options
}: Props) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.attributeRenderer'
  });
  if (!attributes || attributes.length === 0) {
    return <VStack className="items-center py-8">
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          {t('noAttributes')}
        </Text>
      </VStack>;
  }
  const sortedAttributes = [...attributes].sort((a, b) => a.displayOrder - b.displayOrder);
  const renderedComponents = sortedAttributes.map(attribute => renderAttributeComponent(attribute, sectionKey, updateSectionAttribute, storeValues, provisionalValueTypeRenderer, attributeRenderInterceptor, options));
  return <>{renderedComponents}</>;
}

/**
 * 属性のvalueTypeに基づいて適切なCFormコンポーネントをレンダリング
 *
 * ## サポートされるvalueType
 * - TEXT: テキスト入力
 * - TEXT_AREA: 複数行テキスト入力
 * - MULTI_TEXT: 複数行テキスト入力
 * - NUMBER: 数値入力
 * - BOOLEAN: ブール値入力
 * - SELECT: 選択（セレクトボックス）
 * - MULTI_SELECT: 複数選択（マルチセレクト）
 * - DATE: 日付入力
 * - DEVICE_CATALOG_SELECT: デバイスカタログ参照・選択
 * - PERSON_SELECT: 人物選択（コンボボックス）
 * - PLACE_SELECT: 拠点選択（セレクトボックス）
 * - PLACE_MULTI_SELECT: 拠点複数選択（マルチセレクト）
 * - POSTAL_CODE: 郵便番号入力
 * - PHONE_NUMBER: 電話番号入力
 * - EMAIL_ADDRESS: メールアドレス入力
 * - PERSON_NAME: 氏名入力（姓・名・姓カナ・名カナ）
 * - COUNTRY: 国選択（セレクトボックス）
 * - MULTI_COUNTRIES: 複数国選択（マルチセレクト）
 * - STATE_PROVINCE: 都道府県選択（セレクトボックス）
 * - PROVISIONAL: カスタム（ドメイン独自のレンダリング）
 * - BADGE: バッジ表示
 * - URL: URL表示（クリック可能リンク）
 * - FOLDER_LINK: フォルダリンク表示
 * - FILE_LIST: ファイル一覧表示
 *
 * ## 拡張方法
 * 新しいvalueTypeを追加する場合：
 * 1. バックエンドのスキーマにvalueTypeを追加（型は自動同期）
 * 2. 対応するCFormItemコンポーネントを作成（または既存のものを使用）
 * 3. switch文に新しいcaseを追加
 */

/**
 * AMOUNT_WITH_CURRENCY 属性の value 形。金額と通貨コードを保持する。
 */
type AmountWithCurrencyValue = {
  amount: number | null;
  currencyCode: string | null;
};
function isAmountWithCurrencyValue(value: unknown): value is AmountWithCurrencyValue {
  return typeof value === 'object' && value !== null && 'amount' in value && 'currencyCode' in value;
}
function renderAttributeComponent(attribute: Attribute, sectionKey: string, updateSectionAttribute: UpdateSectionAttribute, storeValues: StoreValues, provisionalValueTypeRenderer?: ProvisionalValueTypeRenderer, attributeRenderInterceptor?: AttributeRenderInterceptor, options?: ValueTypeOptions): React.ReactElement | null {
  // ストアから現在の値を取得（更新された値を優先）
  const currentAttribute = getCurrentAttribute(attribute.attributeKey, sectionKey, storeValues);
  const attr = currentAttribute || attribute;

  /**
   * 各 CForm コンポーネントの onChange から渡される値をストアへ中継する。
   * Date→ISO文字列、undefined/null→null の変換を行う。
   * コンポーネント固有の型（PersonNameValue, Address 等）は API スキーマの Value と
   * 構造的に互換だが厳密には一致しないため、unknown で受けて Value にキャストする。
   */
  const handleValueChange = (newValue: unknown) => {
    if (newValue === undefined || newValue === null) {
      updateSectionAttribute(sectionKey, String(attribute.attributeKey), null);
      return;
    }
    if (newValue instanceof Date) {
      updateSectionAttribute(sectionKey, String(attribute.attributeKey), newValue.toISOString() as Value);
      return;
    }
    updateSectionAttribute(sectionKey, String(attribute.attributeKey), newValue as Value);
  };
  const baseProps = getBaseProps(attr);
  const intercepted = attributeRenderInterceptor?.({
    sectionKey,
    attr,
    baseProps,
    handleValueChange,
    updateSectionAttribute,
    storeValues
  });
  if (intercepted != null) {
    return intercepted;
  }

  // valueTypeに基づいてコンポーネントを選択
  switch (attr.valueType) {
    case 'DATE':
      {
        const dateValue = coerceAttributeValue(attr.value, 'DATE');
        return <CFormDateItem key={String(attr.attributeKey)} {...baseProps} value={dateValue} onChange={value => handleValueChange(value)} allowEmpty={!attr.required} />;
      }
    case 'BIRTHDAY':
      {
        const birthdayValue = coerceAttributeValue(attr.value, 'BIRTHDAY');
        return <CFormBirthdayItem key={String(attr.attributeKey)} {...baseProps} value={birthdayValue} onChange={value => handleValueChange(value)} />;
      }
    case 'DATE_RANGE':
      {
        const dateRange = coerceAttributeValue(attr.value, 'DATE_RANGE');
        return <HStack>
          <CFormDateItem key={String(attr.attributeKey) + 'from'} {...baseProps} value={dateRange.startDate ? new Date(dateRange.startDate.year, dateRange.startDate.month - 1, dateRange.startDate.day) : undefined} onChange={value => handleValueChange({
            ...dateRange,
            startDate: value ? {
              year: Number(value.split('-')[0]),
              month: Number(value.split('-')[1]),
              day: Number(value.split('-')[2])
            } : null
          })} allowEmpty={!attr.required} />
          <CFormDateItem key={String(attr.attributeKey) + 'to'} {...baseProps} value={dateRange.endDate ? new Date(dateRange.endDate.year, dateRange.endDate.month - 1, dateRange.endDate.day) : undefined} onChange={value => handleValueChange({
            ...dateRange,
            endDate: value ? {
              year: Number(value.split('-')[0]),
              month: Number(value.split('-')[1]),
              day: Number(value.split('-')[2])
            } : null
          })} allowEmpty={!attr.required} />
        </HStack>;
      }
    case 'DATE_TIME':
      {
        const dateTimeValue = coerceAttributeValue(attr.value, 'DATE_TIME');
        return <CFormDateTimeItem key={String(attr.attributeKey)} {...baseProps} value={dateTimeValue} onChange={value => handleValueChange(value)} allowEmpty={!attr.required} />;
      }
    case 'TEXT':
      {
        const textValue = coerceAttributeValue(attr.value, 'TEXT');
        return <CFormTextItem key={String(attr.attributeKey)} {...baseProps} value={textValue ?? ''} onChange={value => handleValueChange(value ?? '')} />;
      }
    case 'MULTI_TEXT':
      {
        const multiTextValue = coerceAttributeValue(attr.value, 'MULTI_TEXT');
        return <CFormMultiTextItem key={String(attr.attributeKey)} {...baseProps} value={multiTextValue ?? []} onChange={value => handleValueChange(value ?? '')} />;
      }
    case 'TEXT_AREA':
      {
        const textAreaValue = coerceAttributeValue(attr.value, 'TEXT_AREA');
        return <CFormTextAreaItem key={String(attr.attributeKey)} {...baseProps} value={textAreaValue ?? ''} onChange={value => handleValueChange(value ?? '')} />;
      }
    case 'NUMBER':
      {
        const numberValue = coerceAttributeValue(attr.value, 'NUMBER');
        return <CFormNumberItem key={String(attr.attributeKey)} {...baseProps} value={numberValue} onChange={value => handleValueChange(value ?? null)} />;
      }
    case 'BYTE_SIZE':
      {
        const byteSizeValue = coerceAttributeValue(attr.value, 'BYTE_SIZE');
        return <CFormByteSizeItem key={String(attr.attributeKey)} {...baseProps} value={byteSizeValue} onChange={value => handleValueChange(value ?? undefined)} />;
      }
    case 'BOOLEAN':
      {
        const boolValue = coerceAttributeValue(attr.value, 'BOOLEAN');
        return <CFormSwitchItem key={String(attr.attributeKey)} {...baseProps} value={boolValue ?? false} onChange={value => handleValueChange(value ?? null)} />;
      }
    case 'SELECT':
      {
        const selectValue = coerceAttributeValue(attr.value, 'SELECT') ?? '';
        const displayValue = attr.displayValue ?? attr.options?.find(option => option.value === selectValue)?.label ?? '';
        return <CFormSelectItem key={String(attr.attributeKey)} {...baseProps} displayValue={displayValue} value={selectValue} onChange={value => handleValueChange(value || null)} fetchOptions={async () => attr.options ?? []} />;
      }
    case 'GRADE':
      {
        const gradeObj = coerceAttributeValue(attr.value, 'GRADE');
        return <CFormGradeItem key={String(attr.attributeKey)} {...baseProps} value={gradeObj.gradeId ?? undefined} displayValue={gradeObj.name ?? undefined} onChange={(id, name) => handleValueChange(id ? {
          gradeId: id,
          name: name ?? ''
        } : null)} allowEmpty={!attr.required} />;
      }
    case 'JOB_TITLE':
      {
        const jobTitleObj = coerceAttributeValue(attr.value, 'JOB_TITLE');
        return <CFormJobTitleItem key={String(attr.attributeKey)} {...baseProps} value={jobTitleObj.jobTitleId ?? undefined} displayValue={jobTitleObj.name ?? undefined} onChange={(id, name) => handleValueChange(id ? {
          jobTitleId: id,
          name: name ?? ''
        } : null)} allowEmpty={!attr.required} />;
      }
    case 'JOB_TYPE_LIST':
      {
        const jobTypeListValue = coerceAttributeValue(attr.value, 'JOB_TYPE_LIST');
        return <CFormJobTypeListItem key={String(attr.attributeKey)} {...baseProps} value={jobTypeListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'JOB_FUNCTION_LIST':
      {
        const jobFunctionListValue = coerceAttributeValue(attr.value, 'JOB_FUNCTION_LIST');
        return <CFormJobFunctionListItem key={String(attr.attributeKey)} {...baseProps} value={jobFunctionListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'JOB_LEVEL':
      {
        const jobLevelObj = coerceAttributeValue(attr.value, 'JOB_LEVEL');
        return <CFormJobLevelItem key={String(attr.attributeKey)} {...baseProps} value={jobLevelObj.jobLevelId ?? undefined} displayValue={jobLevelObj.name ?? undefined} onChange={(id, name) => handleValueChange(id ? {
          jobLevelId: id,
          name: name ?? ''
        } : null)} allowEmpty={!attr.required} />;
      }
    case 'JOB_COMPETENCIE':
      {
        const jobCompetencieObj = coerceAttributeValue(attr.value, 'JOB_COMPETENCIE');
        return <CFormJobCompetencieItem key={String(attr.attributeKey)} {...baseProps} value={jobCompetencieObj.jobCompetencyId ?? undefined} displayValue={jobCompetencieObj.name ?? undefined} onChange={(id, name) => handleValueChange(id ? {
          jobCompetencyId: id,
          name: name ?? ''
        } : null)} allowEmpty={!attr.required} />;
      }
    case 'JOB_GRADE':
      {
        const jobGradeObj = coerceAttributeValue(attr.value, 'JOB_GRADE');
        return <CFormJobGradeItem key={String(attr.attributeKey)} {...baseProps} value={jobGradeObj.jobGradeId ?? undefined} displayValue={jobGradeObj.name ?? undefined} onChange={(id, name) => handleValueChange(id ? {
          jobGradeId: id,
          name: name ?? ''
        } : null)} allowEmpty={!attr.required} />;
      }
    case 'WORK_MODE':
      {
        const workModeObj = coerceAttributeValue(attr.value, 'WORK_MODE');
        return <CFormWorkModeItem key={String(attr.attributeKey)} {...baseProps} value={workModeObj.workModeId ?? undefined} displayValue={workModeObj.name ?? undefined} onChange={(id, name) => handleValueChange(id ? {
          workModeId: id,
          name: name ?? ''
        } : null)} allowEmpty={!attr.required} />;
      }
    case 'WORK_LOCATION':
      {
        const workLocationValue = coerceAttributeValue(attr.value, 'WORK_LOCATION');
        return <CFormWorkLocationItem key={String(attr.attributeKey)} {...baseProps} value={workLocationValue} onChange={value => handleValueChange(value)} allowEmpty={!attr.required} />;
      }
    case 'MULTI_SELECT':
      {
        const multiSelectValue = coerceAttributeValue(attr.value, 'MULTI_SELECT');
        const providedOptions = attr.options ?? [];
        // 選択済みだが options に存在しない値（廃止された選択肢など）でも表示できるよう、
        // value に同梱された { value, label } で options を補完する。
        // バックエンドが value/label 形式で返すのはこのケースに対応するため。
        const rawMultiSelectValue: unknown = attr.value;
        const selectedWithLabels = (Array.isArray(rawMultiSelectValue) ? rawMultiSelectValue : []).filter((item: unknown): item is {
          value: string;
          label: string;
        } => item != null && typeof item === 'object' && 'value' in item && typeof (item as Record<string, unknown>).value === 'string' && 'label' in item && typeof (item as Record<string, unknown>).label === 'string');
        const multiSelectOptions = [...providedOptions, ...selectedWithLabels.filter(selected => !providedOptions.some(option => option.value === selected.value))];
        const optionLabelMap = new Map(multiSelectOptions.map(option => [option.value, option.label]));
        return <CFormMultiSelectItem key={String(attr.attributeKey)} {...baseProps} value={multiSelectValue ?? []}
        // ストアには API レスポンスと同じ { value, label }[] 形式で保持する。
        // string[] のまま送ると更新リクエストで値が反映されないため、
        // 選択肢からラベルを引いてオブジェクト配列に変換する。
        onChange={value => handleValueChange((value ?? []).map(selectedValue => ({
          value: selectedValue,
          label: optionLabelMap.get(selectedValue) ?? selectedValue
        })))} options={multiSelectOptions} />;
      }
    case 'PERSON_SELECT':
      {
        const personSelectValue = coerceAttributeValue(attr.value, 'PERSON_SELECT');
        return <CFormPersonSelectItem key={String(attr.attributeKey)} {...baseProps} value={personSelectValue} onChange={value => handleValueChange(value ?? null)} showProfileLink={options?.showProfileLink ?? true} category={options?.personSelectCategory} allowEmpty={!attr.required} />;
      }
    case 'DEVICE_CATALOG_SELECT':
      {
        const deviceCatalogId = coerceAttributeValue(attr.value, 'DEVICE_CATALOG_SELECT');
        return <CFormDeviceCatalogSelectItem key={String(attr.attributeKey)} {...baseProps} value={deviceCatalogId} displayValue={attr.displayValue ?? undefined} deviceGenre={options?.deviceCatalogGenre} allowEmpty={!attr.required} onChange={catalog => updateSectionAttribute(sectionKey, String(attribute.attributeKey), catalog?.id ?? null, catalog?.name ?? null)} />;
      }
    case 'PLACE_SELECT':
      {
        const placeSelectValue = coerceAttributeValue(attr.value, 'PLACE_SELECT');
        return <CFormPlaceSelectItem key={String(attr.attributeKey)} {...baseProps} value={placeSelectValue} onChange={value => handleValueChange(value ?? null)} showPlaceLink={options?.showPlaceLink ?? true} allowEmpty={!attr.required} />;
      }
    case 'DEPARTMENT_SELECT':
      {
        const departmentSelectValue = coerceAttributeValue(attr.value, 'DEPARTMENT_SELECT');
        return <CFormDepartmentSelectItem key={String(attr.attributeKey)} {...baseProps} value={departmentSelectValue} onChange={value => handleValueChange(value ?? '')} />;
      }
    case 'EMPLOYMENT_TYPE_SELECT':
      {
        const employmentTypeValue = coerceAttributeValue(attr.value, 'EMPLOYMENT_TYPE_SELECT');
        return <CFormWorkerTypeSelectItem key={attribute.attributeKey} {...baseProps} workerType="EMPLOYEE" value={employmentTypeValue} displayValue={attr.displayValue || undefined} onChange={(id, name) => {
          updateSectionAttribute(sectionKey, attribute.attributeKey, id, name);
        }} />;
      }
    case 'CONTRACTOR_TYPE_SELECT':
      {
        const contractorTypeValue = coerceAttributeValue(attr.value, 'CONTRACTOR_TYPE_SELECT');
        return <CFormWorkerTypeSelectItem key={attribute.attributeKey} {...baseProps} workerType="CONTRACTOR" value={contractorTypeValue} displayValue={attr.displayValue || undefined} onChange={(id, name) => {
          updateSectionAttribute(sectionKey, attribute.attributeKey, id, name);
        }} />;
      }
    case 'PLACE_MULTI_SELECT':
      {
        const placeMultiSelectValue = coerceAttributeValue(attr.value, 'PLACE_MULTI_SELECT');
        return <CFormPlaceMultiSelectItem key={String(attr.attributeKey)} {...baseProps} value={placeMultiSelectValue} onChange={value => handleValueChange(value ?? null)} showPlaceLink={options?.showPlaceLink ?? true} allowEmpty={!attr.required} />;
      }
    case 'POSTAL_CODE':
      {
        const postalCodeValue = coerceAttributeValue(attr.value, 'POSTAL_CODE');
        return <CFormPostalCodeItem key={String(attr.attributeKey)} {...baseProps} value={postalCodeValue ?? ''} onChange={value => handleValueChange(value ?? '')} />;
      }
    case 'PHONE_NUMBER':
      {
        const phoneNumberValue = coerceAttributeValue(attr.value, 'PHONE_NUMBER');
        return <CFormPhoneItem key={String(attr.attributeKey)} {...baseProps} value={phoneNumberValue} onChange={value => handleValueChange(value)} showPhoneType />;
      }
    case 'EMAIL_ADDRESS':
      {
        const emailValue = coerceAttributeValue(attr.value, 'EMAIL_ADDRESS');
        return <CFormEmailItem key={String(attr.attributeKey)} {...baseProps} value={emailValue ?? ''} onChange={value => handleValueChange(value ?? '')} />;
      }
    case 'PERSON_NAME':
      {
        const personNameValue = coerceAttributeValue(attr.value, 'PERSON_NAME');
        // fieldKey はバックエンドが正しい値（familyName 等）を返す前提
        const childAttributes = attr.children as PersonNameChildAttribute[] | undefined;
        return <CFormPersonNameItem key={String(attr.attributeKey)} {...baseProps} value={personNameValue} onChange={value => handleValueChange(value)} childAttributes={childAttributes} />;
      }
    case 'COUNTRY':
      {
        const countryValue = coerceAttributeValue(attr.value, 'COUNTRY');
        return <CFormCountryItem key={String(attr.attributeKey)} {...baseProps} value={countryValue ? {
          code: countryValue
        } : undefined} onChange={value => handleValueChange(value?.code ?? null)} allowEmpty={true} />;
      }
    case 'MULTI_COUNTRIES':
      {
        const multiCountriesValue = coerceAttributeValue(attr.value, 'MULTI_COUNTRIES');
        return <CFormMultiCountryItem key={String(attr.attributeKey)} {...baseProps} value={multiCountriesValue ? multiCountriesValue.map(v => ({
          code: v
        })) : []} onChange={value => handleValueChange(value?.map(v => v?.code) ?? [])} readonly={baseProps.readonly} />;
      }
    case 'STATE_PROVINCE':
      {
        const countryCode = findAttributeByValueType('COUNTRY', sectionKey, storeValues)?.value as SupportedCountry | undefined;
        const stateValue = coerceAttributeValue(attr.value, 'STATE_PROVINCE');
        return <CFormStateProvinceItem key={String(attr.attributeKey)} countryCode={countryCode} {...baseProps} value={stateValue ?? null} onChange={value => handleValueChange(value ?? null)} allowEmpty={!attr.required} />;
      }
    case 'ADDRESS':
      {
        const addressValue = coerceAttributeValue(attr.value, 'ADDRESS');
        return <CFormAddressItem key={String(attr.attributeKey)} {...baseProps} value={addressValue} onChange={value => handleValueChange(value)} />;
      }
    case 'PROVISIONAL':
      {
        return provisionalValueTypeRenderer?.(attr, baseProps, getCurrentAttribute) ?? null;
      }
    case 'BADGE':
      {
        const badgeValue = coerceAttributeValue(attr.value, 'BADGE');
        return <CFormBadgeItem key={String(attr.attributeKey)} {...baseProps} text={badgeValue?.text ?? null} variant={badgeValue?.variant ?? 'neutralMuted'} icon={badgeValue?.icon} />;
      }
    case 'URL':
      {
        const urlValue = coerceAttributeValue(attr.value, 'URL');
        return <CFormUrlItem key={String(attr.attributeKey)} {...baseProps} url={urlValue?.url ?? null} displayText={urlValue?.displayText} />;
      }
    case 'FOLDER_LINK':
      {
        const folderLinkValue = coerceAttributeValue(attr.value, 'FOLDER_LINK');
        return <CFormFolderLinkItem key={String(attr.attributeKey)} {...baseProps} folderId={folderLinkValue?.folderId ?? null} folderName={folderLinkValue?.folderName ?? null} linkTo={folderLinkValue?.linkTo} />;
      }
    case 'FILE':
      {
        const fileValue = coerceAttributeValue(attr.value, 'FILE');
        return <CFormFileItem key={String(attr.attributeKey)} {...baseProps} onChange={value => handleValueChange(value ?? null)} value={fileValue ?? []} maxSize={attr.fileSetting?.maxFileSize} maxFiles={attr.fileSetting?.maxCount} accept={attr.fileSetting?.mimeTypes} />;
      }
    case 'FILE_LIST':
      {
        const fileListValue = coerceAttributeValue(attr.value, 'FILE_LIST');
        return <CFormFileListItem key={String(attr.attributeKey)} {...baseProps} files={fileListValue?.files ?? []} totalCount={fileListValue?.totalCount ?? 0} />;
      }
    case 'IMAGE':
      {
        const imageValue = coerceAttributeValue(attr.value, 'IMAGE');
        return <CFormImageItem key={String(attr.attributeKey)} {...baseProps} image={imageValue ?? null} onViewImage={options?.onViewImage} />;
      }
    case 'NUMBER_WITH_UNIT':
      {
        const numberWithUnitValue = coerceAttributeValue(attr.value, 'NUMBER_WITH_UNIT');
        return <CFormNumberWithUnitItem key={String(attr.attributeKey)} {...baseProps} value={numberWithUnitValue ?? undefined} onChange={value => handleValueChange(value ?? null)} options={attr.options ?? []} />;
      }
    case 'UNIT_PRICES':
      {
        // TODO: 必要になったら実装
        console.warn(`Unsupported valueType: ${attr.valueType} for attribute: ${String(attr.attributeKey)}`);
        return null;
      }
    case 'PHONE_NUMBER_LIST':
      {
        const phoneNumberListValue = coerceAttributeValue(attr.value, 'PHONE_NUMBER_LIST');
        return <CFormPhoneListItem key={String(attr.attributeKey)} {...baseProps} value={phoneNumberListValue} onChange={value => handleValueChange(value)} showPhoneType />;
      }
    case 'EMAIL_ADDRESS_LIST':
      {
        const emailListValue = coerceAttributeValue(attr.value, 'EMAIL_ADDRESS_LIST');
        return <CFormEmailListItem key={String(attr.attributeKey)} {...baseProps} value={emailListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'EMPLOYEE_SUB_ID_LIST':
      {
        const subIdListValue = coerceAttributeValue(attr.value, 'EMPLOYEE_SUB_ID_LIST');
        return <CFormEmployeeSubIdListItem key={String(attr.attributeKey)} {...baseProps} value={subIdListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'DEPARTMENT_LIST':
      {
        const departmentListValue = coerceAttributeValue(attr.value, 'DEPARTMENT_LIST');
        return <CFormDepartmentListItem key={String(attr.attributeKey)} {...baseProps} value={departmentListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'PLACE_MULTI_SELECT_WITH_PRIMARY':
      {
        const placeValues = coerceAttributeValue(attr.value, 'PLACE_MULTI_SELECT_WITH_PRIMARY');
        return <CFormPlaceMultiSelectWithPrimaryItem key={String(attr.attributeKey)} {...baseProps} value={placeValues} onChange={value => handleValueChange(value)} />;
      }
    case 'SNS_ACCOUNT_LIST':
      {
        const snsListValue = coerceAttributeValue(attr.value, 'SNS_ACCOUNT_LIST');
        return <CFormSnsAccountListItem key={String(attr.attributeKey)} {...baseProps} value={snsListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'EMERGENCY_CONTACT_LIST':
      {
        const emergencyContactListValue = coerceAttributeValue(attr.value, 'EMERGENCY_CONTACT_LIST');
        return <CFormEmergencyContactListItem key={String(attr.attributeKey)} {...baseProps} value={emergencyContactListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'FAMILY_PERSONAL_INFO_LIST':
      {
        const familyPersonalInfoListValue = coerceAttributeValue(attr.value, 'FAMILY_PERSONAL_INFO_LIST');
        return <CFormFamilyPersonalInfoListItem key={String(attr.attributeKey)} {...baseProps} value={familyPersonalInfoListValue} onChange={value => handleValueChange(value)} personId={options?.personId} />;
      }
    case 'CONTRACT':
      {
        const contractValue = coerceAttributeValue(attr.value, 'CONTRACT');
        return <CFormContractItem key={String(attr.attributeKey)} {...baseProps} value={contractValue} onChange={value => handleValueChange(value)} />;
      }
    case 'EXTENSION_NUMBER_LIST':
      {
        const extensionValues = coerceAttributeValue(attr.value, 'EXTENSION_NUMBER_LIST');
        return <CFormExtensionNumberListItem key={String(attr.attributeKey)} {...baseProps} value={extensionValues} onChange={value => handleValueChange(value)} />;
      }
    case 'BANK_ACCOUNT':
      {
        const bankValue = coerceAttributeValue(attr.value, 'BANK_ACCOUNT');
        return <CFormBankAccountItem key={String(attr.attributeKey)} {...baseProps} value={bankValue} onChange={value => handleValueChange(value)} />;
      }
    case 'COMMUTE_ROUTE_LIST':
      {
        const commuteValues = coerceAttributeValue(attr.value, 'COMMUTE_ROUTE_LIST');
        return <CFormCommuteRouteListItem key={String(attr.attributeKey)} {...baseProps} value={commuteValues} onChange={value => handleValueChange(value)} />;
      }
    case 'HOMETOWN_LIST':
      {
        const hometownListValue = coerceAttributeValue(attr.value, 'HOMETOWN_LIST');
        return <CFormHometownListItem key={String(attr.attributeKey)} {...baseProps} value={hometownListValue} onChange={value => handleValueChange(value)} />;
      }
    case 'LEAVE_OF_ABSENCE_LIST':
      {
        const leaveOfAbsenceListValue = coerceAttributeValue(attr.value, 'LEAVE_OF_ABSENCE_LIST');
        return <CFormLeaveOfAbsenceListItem key={String(attr.attributeKey)} {...baseProps} value={leaveOfAbsenceListValue} />;
      }
    case 'BP_SELECT':
      return <CFormBpSelectItem key={String(attr.attributeKey)} {...baseProps} value={typeof attr.value === 'string' ? attr.value : undefined} onChange={value => handleValueChange(value ?? null)} allowEmpty={!attr.required} showDetailLink />;
    case 'AMOUNT_WITH_CURRENCY':
      {
        const amountWithCurrency = isAmountWithCurrencyValue(attr.value) ? attr.value : {
          amount: null,
          currencyCode: null
        };
        return <CFormCurrencyItem key={String(attr.attributeKey)} {...baseProps} value={amountWithCurrency.amount ?? undefined} currencyCode={amountWithCurrency.currencyCode ?? ''} onChange={amount => handleValueChange({
          amount: amount ?? null,
          currencyCode: amountWithCurrency.currencyCode
        })} />;
      }
    case 'TODO':
      throw new Error(`Unsupported valueType: ${attr.valueType} for attributeKey: ${attr.attributeKey}`);
    default:
      {
        const notSupportedValueType: never = attr.valueType;
        console.warn(`Unsupported valueType: ${notSupportedValueType} for attribute: ${String(attr.attributeKey)}`);
        const fallbackValue = coerceAttributeValue(attr.value, attr.valueType);
        return <CFormTextItem key={String(attr.attributeKey)} {...baseProps} value={fallbackValue != null ? String(fallbackValue) : ''} onChange={value => handleValueChange(value)} />;
      }
  }
}

/**
 * ベースプロパティを取得
 *
 * CFormItemコンポーネントの共通プロパティを生成します。
 */
function getBaseProps(attribute: Attribute): BaseProps {
  // 拡張プロパティ（titleIcon）を取得
  const extendedAttr = attribute as Attribute & {
    titleIcon?: typeof IconMap extends Record<infer K, unknown> ? K : never;
  };
  return {
    id: String(attribute.attributeKey),
    label: String(attribute.name),
    required: Boolean(attribute.required),
    readonly: Boolean(attribute.readonly),
    // colspanが指定されている場合はColSpan型にキャスト（1-7の範囲）
    colSpan: attribute.colspan as 1 | 2 | 3 | 4 | 5 | 6 | 7 | undefined,
    titleIcon: extendedAttr.titleIcon
  };
}

/**
 * ストアから現在の属性を取得
 *
 * セクションキーと属性キーに基づいて、ストアから最新の属性データを取得します。
 * 編集中の値とAPIから取得した値を適切に管理するために使用します。
 */
function getCurrentAttribute(attributeKey: string, sectionKey: string, storeValues: StoreValues): Attribute | undefined {
  const section = storeValues.sections?.find(s => String(s.sectionKey) === sectionKey);
  if (section) {
    return section.attributes.find(a => String(a.attributeKey) === String(attributeKey));
  }
  return undefined;
}

/**
 * ストアから特定のValueTypeのAttributeを取得する
 *
 * セクションキーとValueTypeに基づいて、ストアから最新の属性データを取得します。
 */
function findAttributeByValueType(valueType: ValueType, sectionKey: string, storeValues: StoreValues): Attribute | undefined {
  const section = storeValues.sections?.find(s => String(s.sectionKey) === sectionKey);
  if (section) {
    return section.attributes.find(a => a.valueType === valueType);
  }
  return undefined;
}